import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import {
  ArrowRight,
  BanknoteCheck,
  Clock,
  Groceries,
  ImageOff,
  MapPin,
  type LucideIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { type Deposit } from "../../../Deposits/DepositCard/deposit-dtos";
import { DepositDetailsModal } from "../../../Deposits/DepositCard/DepositDetailsModal";
import { depositPagePath } from "../../../Deposits/DepositDetails/utils";
import { type Offer } from "../../../RepresentativeOffers/components/OfferCard/interfaces";
import { OfferCommentsModal } from "../../../RepresentativeOffers/components/OfferCommentsModal";
import { type DeliveryLookups } from "../../../RepresentativeOffers/types";
import { depositToHighlight, offerToHighlight } from "./adapter";
import { type HighlightCardData } from "./types";

const KEY = "home.manager.overview.highlights";

const HighlightCard = ({ item, onSelect }: { item: HighlightCardData; onSelect: () => void }) => (
  <Card className="group relative gap-0 overflow-hidden py-0 transition-[translate,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring motion-reduce:transition-none motion-reduce:hover:translate-y-0">
    <div className="relative aspect-[16/10] overflow-hidden bg-muted">
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt=""
          className="absolute inset-0 size-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
        />
      ) : (
        <div className="flex size-full items-center justify-center text-accent-foreground">
          <ImageOff className="size-10" aria-hidden="true" />
        </div>
      )}
      {item.badge && (
        <Badge variant={item.badge.variant} className="absolute top-3 left-3">
          {item.badge.label}
        </Badge>
      )}
      {item.location && (
        <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-foreground text-xs">
          <MapPin className="size-3.5 text-blue-600" aria-hidden="true" />
          {item.location}
        </span>
      )}
    </div>

    <div className="flex flex-1 flex-col gap-3 p-4">
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={onSelect}
          className="cursor-pointer text-left font-semibold text-foreground text-lg after:absolute after:inset-0 after:z-10 focus-visible:outline-none"
        >
          {item.title}
        </button>
        <p className="line-clamp-2 break-words text-muted-foreground text-sm" title={item.subtitle}>
          {item.subtitle}
        </p>
        <p className="text-muted-foreground text-sm">{item.date}</p>
      </div>

      <dl className="grid grid-cols-3 gap-2">
        {item.metrics.map(({ label, value }) => (
          <div key={label} className="flex min-w-0 flex-col">
            <dt className="text-muted-foreground text-sm">{label}</dt>
            <dd className="break-words font-semibold text-foreground text-sm">{value}</dd>
          </div>
        ))}
      </dl>

      {item.footer && (
        <p className="mt-auto flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-foreground text-sm">
          <Clock className="size-4 shrink-0" aria-hidden="true" />
          {item.footer}
        </p>
      )}
    </div>
  </Card>
);

const HighlightSection = ({
  sectionKey,
  icon: Icon,
  to,
  items,
  isLoading,
  onSelect,
}: {
  sectionKey: "fresh" | "market";
  icon: LucideIcon;
  to: string;
  items: HighlightCardData[];
  isLoading: boolean;
  onSelect: (id: number) => void;
}) => {
  const { t } = useTranslation();
  const isEmpty = !isLoading && !items.length;

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <div className="flex items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="flex items-center gap-2 font-semibold text-xl">
            <Icon className="size-7 shrink-0 text-blue-600" aria-hidden="true" />
            {t(`${KEY}.${sectionKey}.title`)}
          </h2>
          <p className="text-muted-foreground">
            {t(`${KEY}.${sectionKey}.${isEmpty ? "emptyDescription" : "subtitle"}`)}
          </p>
        </div>
        {!isEmpty && (
          <WFPButton variant="link" className="shrink-0" asChild>
            <Link to={to}>
              {t(`${KEY}.viewAll`)}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </WFPButton>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-96 rounded-xl" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      ) : (
        !isEmpty && (
          <div className="grid gap-4 sm:grid-cols-2">
            {items.map(item => (
              <HighlightCard key={item.id} item={item} onSelect={() => onSelect(item.id)} />
            ))}
          </div>
        )
      )}
    </section>
  );
};

interface MarketHighlightsProps {
  deposits: Deposit[];
  isFetchingDeposits: boolean;
  offers: Offer[];
  isFetchingOffers: boolean;
  deliveryLookups: DeliveryLookups;
  onOpenOffer: (offer: Offer) => void;
}

const noop = async () => {};

export const MarketHighlights = ({
  deposits,
  isFetchingDeposits,
  offers,
  isFetchingOffers,
  deliveryLookups,
  onOpenOffer,
}: MarketHighlightsProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedDeposit, setSelectedDeposit] = useState<Deposit>();
  const [selectedOffer, setSelectedOffer] = useState<Offer>();

  const selectOffer = (id: number) => {
    const offer = offers.find(item => item.id === id);
    if (offer) {
      onOpenOffer(offer);
      setSelectedOffer(offer);
    }
  };

  return (
    <div className="grid gap-8 xl:grid-cols-2">
      <HighlightSection
        sectionKey="fresh"
        icon={Groceries}
        to="/wfpManager/market"
        items={deposits.map(deposit => depositToHighlight(deposit, t))}
        isLoading={isFetchingDeposits}
        onSelect={id => setSelectedDeposit(deposits.find(item => item.id === id))}
      />
      <HighlightSection
        sectionKey="market"
        icon={BanknoteCheck}
        to="/wfpManager/offers"
        items={offers.map(offer => offerToHighlight(offer, t))}
        isLoading={isFetchingOffers}
        onSelect={selectOffer}
      />

      {selectedDeposit && (
        <DepositDetailsModal
          deposit={selectedDeposit}
          isOpen
          onClose={() => setSelectedDeposit(undefined)}
          onClickAggregatorDetails={id => history.push(`/wfpManager/aggregators/${id}`)}
          detailsPath={depositPagePath(true, selectedDeposit.uuid)}
        />
      )}

      {selectedOffer && (
        <OfferCommentsModal
          offer={selectedOffer}
          deliveryLookups={deliveryLookups}
          readOnly
          onCancel={() => setSelectedOffer(undefined)}
          onRecallOffer={noop}
          onSendBackExchange={noop}
          onCounterOffer={noop}
          setSeeReasonsOffer={setSelectedOffer}
        />
      )}
    </div>
  );
};
